import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { initializePaddle, isPaddleInitialized } from '@/lib/paddle';

interface PaddleContextValue {
  isReady: boolean;
  isLoading: boolean;
  error: Error | null;
}

const PaddleContext = createContext<PaddleContextValue>({
  isReady: false,
  isLoading: true,
  error: null,
});

export const usePaddle = () => useContext(PaddleContext);

export const PaddleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const init = async () => {
      // Skip if already initialized
      if (isPaddleInitialized()) {
        setIsReady(true);
        setIsLoading(false);
        return;
      }

      try {
        console.log('[PaddleProvider] Fetching client token from config...');
        const { data, error: configError } = await supabase.functions.invoke('paddle-public-config', {
          method: 'GET',
        });

        if (configError || !data?.client_token) {
          throw new Error('Failed to fetch Paddle configuration');
        }

        const clientToken = data.client_token;
        const environment: 'production' | 'sandbox' = clientToken.startsWith('live_')
          ? 'production'
          : 'sandbox';

        console.log('[PaddleProvider] Initializing Paddle...', { environment });
        await initializePaddle(clientToken, environment);

        setIsReady(true);
        console.log('[PaddleProvider] ✅ Paddle ready');
      } catch (err) {
        console.error('[PaddleProvider] Initialization failed:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  return (
    <PaddleContext.Provider value={{ isReady, isLoading, error }}>
      {children}
    </PaddleContext.Provider>
  );
};
